import { useState } from 'react';
import { DollarSign, Clock, X, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { ResourcePayrollData } from '../../lib/payrollRuns';

interface PayrollReviewCardsProps {
  payrollData: Record<string, ResourcePayrollData>;
  periodStart: Date;
  periodEnd: Date;
  onResourceRemoved: (resourceId: string) => void;
  onContinue: () => void;
  onCancel: () => void;
}

export default function PayrollReviewCards({
  payrollData,
  periodStart,
  periodEnd,
  onResourceRemoved,
  onContinue,
  onCancel
}: PayrollReviewCardsProps) {
  const [removedResources, setRemovedResources] = useState<Set<string>>(new Set());

  const handleRemoveResource = (resourceId: string) => {
    const newRemoved = new Set(removedResources);
    newRemoved.add(resourceId);
    setRemovedResources(newRemoved);
    onResourceRemoved(resourceId);
  };

  const activeResources = Object.entries(payrollData).filter(
    ([resourceId]) => !removedResources.has(resourceId)
  );

  const totalAmount = activeResources.reduce(
    (sum, [, data]) => sum + data.totalEarnings,
    0
  );

  return (
    <div className="space-y-6">
      {/* Combined Period & Summary */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="mb-3 flex items-center gap-2 border-b border-gray-100 pb-3">
          <Calendar className="h-4 w-4 text-gray-400" />
          <div>
            <p className="text-[12px] text-gray-500">Pay Period</p>
            <p className="text-[14px] font-semibold text-gray-900">
              {format(periodStart, 'MMM d, yyyy')} – {format(periodEnd, 'MMM d, yyyy')}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-[12px] text-gray-500">Total Amount</p>
            <p className="text-[22px] font-bold tabular-nums text-gray-900">${totalAmount.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-[12px] text-gray-500">Employees</p>
            <p className="text-[22px] font-bold text-gray-900">{activeResources.length}</p>
          </div>
        </div>
      </div>

      {/* Resource Cards */}
      <div className="space-y-3">
        {activeResources.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center border border-gray-200">
            <p className="text-gray-500">All resources have been removed from this payroll run.</p>
            <p className="text-sm text-gray-400 mt-2">Add resources back or cancel.</p>
          </div>
        ) : (
          activeResources.map(([resourceId, data]) => {
            const { resource } = data;
            const timeclockEarnings = data.timeclock?.totalEarnings || 0;
            const flatRateEarnings = data.bookings ? Object.values(data.bookings).reduce(
              (sum, booking) =>
                sum +
                (booking?.items || []).reduce(
                  (itemSum: number, item: any) =>
                    itemSum + (item.entry_type === 'flat_rate' ? item.earnings : 0),
                  0
                ),
              0
            ) : 0;
            const commissionEarnings = data.bookings ? Object.values(data.bookings).reduce(
              (sum, booking) =>
                sum +
                (booking?.items || []).reduce(
                  (itemSum: number, item: any) =>
                    itemSum + (item.entry_type === 'commission' ? item.earnings : 0),
                  0
                ),
              0
            ) : 0;
            const bonusEarnings = data.bonuses?.totalEarnings || 0;
            const adjustmentEarnings = data.adjustments?.totalEarnings || 0;

            return (
              <div
                key={resourceId}
                className="overflow-hidden rounded-xl border border-gray-200 bg-white"
              >
                <div className="p-3">
                  {/* Header with Avatar and Remove Button */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {/* Avatar */}
                      <div className="relative">
                        <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-blue-100 text-sm font-semibold text-blue-700">
                          {resource.avatar_url ? (
                            <img 
                              src={resource.avatar_url} 
                              alt={`${resource.first_name} ${resource.last_name}`}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            `${resource.first_name?.[0]}${resource.last_name?.[0]}`
                          )}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-sm text-gray-900 truncate">
                          {resource.first_name} {resource.last_name}
                        </h4>
                        <p className="text-xs text-gray-500 truncate">{resource.email}</p>
                        <span className="mt-0.5 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium capitalize text-gray-600">
                          {resource.compensation_type.replace('_', ' ')}
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={() => handleRemoveResource(resourceId)}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 touch-manipulation"
                      title="Remove from payroll"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Total Earnings */}
                  <div className="mb-2 pb-2 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-600">Total Earnings</span>
                      <span className="text-lg font-bold text-gray-900">
                        ${data.totalEarnings.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Breakdown - Stats Grid */}
                  <div className="grid grid-cols-2 gap-2">
                    {timeclockEarnings > 0 && (
                      <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                        <div className="mb-1 flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5 text-gray-400" />
                          <span className="text-xs font-medium text-gray-500">Clock</span>
                        </div>
                        <div className="text-base font-bold text-gray-900">
                          ${timeclockEarnings.toFixed(2)}
                        </div>
                      </div>
                    )}

                    {flatRateEarnings > 0 && (
                      <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                        <div className="mb-1 flex items-center gap-1.5">
                          <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                          <span className="text-xs font-medium text-gray-500">Flat</span>
                        </div>
                        <div className="text-base font-bold text-gray-900">
                          ${flatRateEarnings.toFixed(2)}
                        </div>
                      </div>
                    )}

                    {commissionEarnings > 0 && (
                      <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                        <div className="mb-1 flex items-center gap-1.5">
                          <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                          <span className="text-xs font-medium text-gray-500">Commission</span>
                        </div>
                        <div className="text-base font-bold text-gray-900">
                          ${commissionEarnings.toFixed(2)}
                        </div>
                      </div>
                    )}

                    {bonusEarnings > 0 && (
                      <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                        <div className="mb-1 flex items-center gap-1.5">
                          <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                          <span className="text-xs font-medium text-gray-500">Bonus</span>
                        </div>
                        <div className="text-base font-bold text-gray-900">
                          ${bonusEarnings.toFixed(2)}
                        </div>
                      </div>
                    )}

                    {adjustmentEarnings !== 0 && (
                      <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                        <div className="mb-1 flex items-center gap-1.5">
                          <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                          <span className="text-xs font-medium text-gray-500">Adjustments</span>
                        </div>
                        <div className={`text-base font-bold ${adjustmentEarnings < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {adjustmentEarnings < 0 ? '-' : '+'}${Math.abs(adjustmentEarnings).toFixed(2)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 border-t border-gray-200 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 rounded-xl bg-gray-100 py-2.5 text-[15px] font-medium text-gray-700 active:bg-gray-200 touch-manipulation"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onContinue}
          disabled={activeResources.length === 0}
          className={`flex-1 rounded-xl py-2.5 text-[15px] font-semibold touch-manipulation ${
            activeResources.length > 0
              ? 'bg-blue-600 text-white active:bg-blue-800'
              : 'cursor-not-allowed bg-gray-200 text-gray-500'
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
